/**
 * The scroll rail's geometry — every number that maps between a pointer on the drawn
 * track and a scroll offset on the table, as pure functions a test can call.
 *
 * This is a separate module because the web suite has no DOM: `vitest.config.ts` runs in
 * `environment: 'node'` and says so out loud — *"Rendering is proved in a real browser,
 * not jsdom."* The rail's three interaction defects were all arithmetic, and arithmetic
 * that only a browser can reach is arithmetic nobody regression-tests. So the maths lives
 * here, the DOM plumbing stays in `TableScrollRail.tsx`, and the browser proves the one
 * thing only a browser can: whether the rail is on screen.
 *
 * ── The bug all three shared ──
 *
 * `trackRef` sat on the PADDED wrapper (`px-4`), not the drawn track inside it, so every
 * measurement was 32px wider than the thing the user was aiming at. Drag lagged the
 * pointer, a track click landed short, and the thumb crept when you clicked it.
 */

export type RailMetrics = {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
};

/**
 * Floor on the thumb's width, as a percentage of the track. A table twenty screens wide
 * would otherwise draw a 5% thumb nobody can grab — and on touch, cannot hit at all.
 */
export const MIN_THUMB_PCT = 8;

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** How far the table can travel. Zero means it fits, and the rail should not render. */
export function maxScrollOf(m: RailMetrics): number {
  return Math.max(0, m.scrollWidth - m.clientWidth);
}

/** The thumb's width as a percentage of the track: how much of the table is on screen. */
export function thumbPctOf(m: RailMetrics): number {
  if (m.scrollWidth <= 0) return 100;
  return clamp((m.clientWidth / m.scrollWidth) * 100, MIN_THUMB_PCT, 100);
}

/**
 * The thumb's left edge as a percentage of the track.
 *
 * `scrollLeft` is clamped rather than trusted: Safari reports values OUTSIDE `[0, max]`
 * during elastic overscroll, which drew the thumb off the end of its own track and then
 * snapped it back — and put `aria-valuenow` outside the `0..100` the element declares.
 */
export function thumbLeftPctOf(m: RailMetrics): number {
  const max = maxScrollOf(m);
  if (max <= 0) return 0;
  return (clamp(m.scrollLeft, 0, max) / max) * (100 - thumbPctOf(m));
}

/** `aria-valuenow`, as the 0–100 the element promises. Same overscroll reason. */
export function ariaValueNow(m: RailMetrics): number {
  const max = maxScrollOf(m);
  if (max <= 0) return 0;
  return Math.round((clamp(m.scrollLeft, 0, max) / max) * 100);
}

/**
 * Where the table should sit when the pointer is `x` px from the drawn track's left edge.
 *
 * This is the exact inverse of {@link thumbLeftPctOf}: solve for the `scrollLeft` whose
 * thumb would be CENTRED under the pointer. That inverse relationship is the whole
 * specification — it is what makes clicking the thumb where it already is a no-op, and
 * what makes the thumb land under your finger rather than near it.
 *
 * ⚠️ The version this replaces mapped the pointer to a position in the CONTENT
 * (`(x / trackWidth) * scrollWidth - clientWidth / 2`) rather than to a position of the
 * THUMB. Algebraically those are the same expression — right up until `thumbPctOf` hits
 * its 8% floor, at which point the content model overshoots by the width of the floor and
 * a click near the right-hand end of a very wide table lands past where the thumb can go.
 * Two models that agree on the common case and diverge on the wide one is the worst of
 * both: it looks correct until the table that most needs a rail is the one it fails on.
 */
export function scrollForPointer(x: number, trackWidth: number, m: RailMetrics): number {
  const max = maxScrollOf(m);
  if (max <= 0 || trackWidth <= 0) return 0;
  const thumbWidth = (thumbPctOf(m) / 100) * trackWidth;
  const usable = trackWidth - thumbWidth;
  // A thumb as wide as its track cannot express a position; there is nowhere to travel.
  if (usable <= 0) return clamp(m.scrollLeft, 0, max);
  return clamp(((x - thumbWidth / 2) / usable) * max, 0, max);
}

/**
 * Where the table should sit after the thumb has been dragged `dx` px from where the
 * drag started. Same usable-width denominator as the click mapping, for the same reason:
 * the thumb has to keep up with the pointer exactly, or the two drift apart over a drag.
 */
export function scrollForDrag(
  dx: number,
  trackWidth: number,
  startScroll: number,
  m: RailMetrics,
): number {
  const max = maxScrollOf(m);
  if (max <= 0 || trackWidth <= 0) return clamp(startScroll, 0, max);
  const usable = trackWidth - (thumbPctOf(m) / 100) * trackWidth;
  if (usable <= 0) return clamp(startScroll, 0, max);
  return clamp(startScroll + (dx / usable) * max, 0, max);
}

/** A `DOM_DELTA_LINE` notch, in px. Firefox's mouse wheel reports 3 of these, not 48px. */
export const LINE_HEIGHT_PX = 16;

/**
 * How far a wheel gesture should move the table, in px — or **0 when the gesture is not
 * the rail's to claim**, which is most of them.
 *
 * ⚠️ Two things this got wrong, both found in review, and both only on the surface the
 * rail actually ships to (it renders only where a table overflows — 390px and 900px, never
 * 1440px on `/customers`):
 *
 * 1. **`deltaMode`.** Firefox reports `DOM_DELTA_LINE` with `deltaY: 3` for a mouse wheel.
 *    Read as pixels that is a 3px crawl — and because 3px *is* movement, the handler would
 *    `preventDefault()` and freeze the page for the ~194 notches it takes to cross the
 *    table. The first version created in Firefox the exact dead zone it was written to
 *    prevent in Chrome.
 * 2. **A plain vertical gesture belongs to the PAGE.** The rail is now glued to the bottom
 *    of the viewport for the whole height of a long list, so treating `deltaY` as
 *    "sideways" turns a 23px band across every screen into a place where scrolling down
 *    stops working until the table runs out of travel. Declining only at the two ends is
 *    not a mitigation — the middle is where people scroll. `Shift` is the platform's own
 *    word for "that wheel, but sideways", so a mouse keeps a wheel gesture; it just has to
 *    ask. Drag, track click and the six keys are all still there.
 */
export function wheelDelta(
  e: { deltaX: number; deltaY: number; deltaMode?: number; shiftKey?: boolean },
  clientWidth: number,
): number {
  const scale =
    e.deltaMode === 1 ? LINE_HEIGHT_PX : e.deltaMode === 2 ? Math.max(1, clientWidth) : 1;
  const dx = e.deltaX * scale;
  const dy = e.deltaY * scale;
  if (Math.abs(dx) > Math.abs(dy)) return dx;
  return e.shiftKey ? dy : 0;
}

/** Keyboard step for the arrow keys, in px of table travel. */
export const KEY_STEP = 60;

/**
 * Where a key press should put the table, or `null` if the rail does not own that key —
 * and `null` is load-bearing: it is what lets Tab, Enter and everything else through
 * instead of being swallowed by a `preventDefault` the rail had no business calling.
 */
export function scrollForKey(key: string, m: RailMetrics): number | null {
  const max = maxScrollOf(m);
  const page = m.clientWidth * 0.9;
  const moves: Record<string, number> = {
    ArrowRight: m.scrollLeft + KEY_STEP,
    ArrowLeft: m.scrollLeft - KEY_STEP,
    PageDown: m.scrollLeft + page,
    PageUp: m.scrollLeft - page,
    Home: 0,
    End: max,
  };
  // `Object.hasOwn`, not `moves[key] === undefined`: `moves['constructor']` reads through
  // the prototype and returns a function, which clamps to NaN, gets `preventDefault`ed and
  // snaps the table to 0. Unreachable from a physical keyboard, and it contradicts the
  // contract stated two lines up — a key the rail does not own must come back null.
  return Object.hasOwn(moves, key) ? clamp(moves[key]!, 0, max) : null;
}
